// Pure derivations for the Command Center dashboard. Everything here reads from
// the SAME live data the rest of the app uses (scheduled posts + dispatch
// history + connected accounts) — never mock data — so the header stats, Today
// rail, and Needs-attention block always agree with the calendar.

import type { ConnectedAccount, DispatchRecord, ScheduledPost } from "../api";
import { isPendingPlatformError } from "./errorMessage";
import { localDayKey } from "./calendarUtils";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** A dispatch leg counts as a real failure only when it isn't a calm "held"
 * (pending-approval) leg — those need no attention. */
function hasRealFailure(rec: DispatchRecord): boolean {
  return rec.results.some((r) => !r.ok && !isPendingPlatformError(r.error));
}

function anyPublished(rec: DispatchRecord): boolean {
  return rec.results.some((r) => r.ok);
}

export interface DashboardStats {
  scheduled: number;
  publishedThisWeek: number;
  failed: number;
}

/** Header stat row: queued count, posts that actually published in the last 7
 * days, and posts with a real failed leg in that window. */
export function computeStats(
  scheduled: ScheduledPost[],
  history: DispatchRecord[],
): DashboardStats {
  const since = Date.now() - WEEK_MS;
  const recent = history.filter((r) => r.at >= since);
  return {
    scheduled: scheduled.length,
    publishedThisWeek: recent.filter(anyPublished).length,
    failed: recent.filter(hasRealFailure).length,
  };
}

/** Scheduled posts landing on the local calendar "today", earliest first. */
export function todaysScheduled(scheduled: ScheduledPost[]): ScheduledPost[] {
  const key = localDayKey(new Date());
  return scheduled
    .filter((p) => localDayKey(new Date(p.at)) === key)
    .sort((a, b) => a.at - b.at);
}

/** Accounts whose stored token can no longer publish — the reconnect prompts. */
export function reconnectAccounts(accounts: ConnectedAccount[]): ConnectedAccount[] {
  return accounts.filter((a) => a.needsReconnect);
}

/** Recent dispatches carrying a real (non-held) failed leg, newest first. */
export function recentFailures(history: DispatchRecord[], limit = 3): DispatchRecord[] {
  return history
    .filter(hasRealFailure)
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
}

/** localStorage key holding the set of dispatch-record ids the user has
 * dismissed from the Needs-attention block. */
const ATTN_DISMISSED_KEY = "broadside.attn.dismissed";

/** Ids of failed dispatches the user has dismissed from Needs-attention.
 * Persisted so a dismissed failure stays gone across refreshes. Any storage or
 * parse failure degrades to an empty set — dismissal is a convenience, never a
 * hard dependency. */
export function readDismissedFailures(): Set<string> {
  try {
    const raw = localStorage.getItem(ATTN_DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

/** Add one dispatch-record id to the dismissed set and persist it, returning the
 * new set so the caller can drive an immediate re-render. */
export function dismissFailure(id: string): Set<string> {
  const next = readDismissedFailures();
  next.add(id);
  try {
    localStorage.setItem(ATTN_DISMISSED_KEY, JSON.stringify([...next]));
  } catch {
    /* non-fatal — the dismissal just won't survive a refresh */
  }
  return next;
}

/** LocalStorage key for published posts the user has cleared from the calendar. */
const CAL_CLEARED_KEY = "broadside.cal.cleared";

/** Ids of published dispatches the user has hidden from the calendar. This only
 * tidies the calendar — the posts stay in Analytics and the published counts —
 * so any storage/parse failure safely degrades to "nothing hidden". */
export function readClearedPosts(): Set<string> {
  try {
    const raw = localStorage.getItem(CAL_CLEARED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function persistClearedPosts(set: Set<string>): void {
  try {
    localStorage.setItem(CAL_CLEARED_KEY, JSON.stringify([...set]));
  } catch {
    /* non-fatal — the change just won't survive a refresh */
  }
}

/** Hide a published dispatch from the calendar; returns the new set. */
export function clearPost(id: string): Set<string> {
  const next = readClearedPosts();
  next.add(id);
  persistClearedPosts(next);
  return next;
}

/** Un-hide a cleared post (Undo); returns the new set. */
export function unclearPost(id: string): Set<string> {
  const next = readClearedPosts();
  next.delete(id);
  persistClearedPosts(next);
  return next;
}

/** Un-hide every cleared post — the "Restore" safety net; returns the empty set. */
export function restoreAllClearedPosts(): Set<string> {
  const empty = new Set<string>();
  persistClearedPosts(empty);
  return empty;
}

/** History rows for the Recent-performance section, newest first. */
export function recentPublished(history: DispatchRecord[], limit = 5): DispatchRecord[] {
  return [...history].sort((a, b) => b.at - a.at).slice(0, limit);
}

/** Best-effort first name from an email local-part, Title-cased. Falls back to
 * "there" so the greeting always reads like a sentence. */
export function firstNameFromEmail(email: string | undefined): string {
  if (!email) return "there";
  const local = email.split("@")[0] ?? "";
  const token = local.split(/[.\-_+]/)[0] ?? "";
  if (!token) return "there";
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/** Time-of-day greeting in the user's local time. */
export function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
